/**
 * Canal WS de notificaciones in-app en vivo (S46 · Wave C, O7).
 *
 * Contrato `GET /ws/notifications`:
 * - Auth con `Sec-WebSocket-Protocol: bearer, <JWT access>` (nunca en la URL,
 *   que queda en los logs). `?userId=` no se acepta desde S46.
 * - Al conectar el server emite `{"type":"notification.ws.ready"}`.
 * - Push: `{"type":"notification.created", id, category, title, body,
 *   priority, status, resourceType, resourceId, createdAt, subject}`.
 * - Close 4401 = token inválido → refrescar token y reconectar.
 *
 * Presencia: el socket solo vive mientras el usuario está presente. Con la
 * pestaña oculta más de {@link HIDDEN_GRACE_MS} o sin actividad durante
 * {@link IDLE_MS} se cierra (Cloud Run factura la instancia de la API mientras
 * haya un WebSocket abierto y eso además impide que Neon se suspenda). Al
 * volver se reconecta y se refrescan las notificaciones para recuperar lo que
 * llegó mientras tanto.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ENV } from "@/config/environment";
import { useAuth } from "@/context/AuthContext";
import { getCurrentAccessToken } from "@/services/auth";
import { wsAuthProtocols } from "@/services/websocketAuth";

const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;
const WS_CLOSE_NORMAL = 1000;

/** Pestaña oculta más de este tiempo → se cierra el socket. */
export const HIDDEN_GRACE_MS = 60_000;
/** Sin actividad del usuario (puntero, teclado, scroll) durante este tiempo → se cierra. */
export const IDLE_MS = 10 * 60_000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "focus",
] as const;

export const NOTIFICATION_CREATED_EVENT = "aequia:notification.created";

export type RealtimeResourceType = "DOCUMENT" | "MATTER" | "CLIENT" | null;

export interface RealtimeNotification {
  type: "notification.created";
  id: string;
  category?: string | null;
  title?: string | null;
  body?: string | null;
  priority?: string | null;
  status?: string | null;
  resourceType?: RealtimeResourceType;
  resourceId?: string | null;
  createdAt?: string | null;
  subject?: string | null;
}

interface RealtimeReadyMessage {
  type: "notification.ws.ready";
}

type RealtimeMessage = RealtimeNotification | RealtimeReadyMessage;

function buildWsUrl(): string {
  const base = ENV.backendUrl.startsWith("http")
    ? ENV.backendUrl
    : `${window.location.origin}${ENV.backendUrl}`;
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname.replace(/\/$/, "")}/ws/notifications`;
  return url.toString();
}

export function useRealtimeNotifications() {
  const { user, currentTenantId } = useAuth();
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const userId = user?.id ?? null;
  const tenantKey = currentTenantId ?? "*";

  useEffect(() => {
    if (!userId) return undefined;
    let closed = false;
    // Usuario ausente (pestaña oculta o inactivo): sin socket ni reintentos.
    let paused = false;
    let connecting = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    let retryTimer: number | null = null;
    let hiddenTimer: number | null = null;
    let idleTimer: number | null = null;

    const clearRetry = () => {
      if (retryTimer) window.clearTimeout(retryTimer);
      retryTimer = null;
    };

    const scheduleReconnect = () => {
      if (closed || paused) return;
      clearRetry();
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void connect();
      }, retry);
      retry = Math.min(8000, retry * 1.8);
    };

    const pause = () => {
      if (paused || closed) return;
      paused = true;
      clearRetry();
      const open = socket;
      socket = null;
      open?.close(WS_CLOSE_NORMAL, "user-away");
      setConnected(false);
    };

    let lastArmed = 0;
    const armIdleTimer = () => {
      lastArmed = Date.now();
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(pause, IDLE_MS);
    };

    const resume = () => {
      if (closed) return;
      // pointermove dispara muy seguido: con el socket activo basta re-armar cada 5 s.
      if (!paused && Date.now() - lastArmed < 5_000) return;
      armIdleTimer();
      if (!paused) return;
      paused = false;
      retry = 800;
      // Lo que llegó mientras el socket estuvo cerrado se recupera por HTTP.
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      void connect();
    };

    const onVisibilityChange = () => {
      if (hiddenTimer) window.clearTimeout(hiddenTimer);
      hiddenTimer = null;
      if (document.visibilityState === "hidden") {
        hiddenTimer = window.setTimeout(pause, HIDDEN_GRACE_MS);
      } else {
        resume();
      }
    };

    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      resume();
    };

    const connect = async () => {
      if (closed || paused || connecting || socket) return;
      connecting = true;
      const token = await getCurrentAccessToken().catch(() => null);
      connecting = false;
      if (closed || paused) return;
      if (!token) {
        setConnected(false);
        scheduleReconnect();
        return;
      }
      const ws = new WebSocket(buildWsUrl(), wsAuthProtocols(token));
      socket = ws;
      ws.onopen = () => {
        retry = 800;
        setConnected(true);
      };
      ws.onclose = (event: CloseEvent) => {
        if (socket === ws) socket = null;
        setConnected(false);
        if (closed || paused) return;
        if (event.code === WS_CLOSE_FORBIDDEN) return;
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          // El próximo connect pasa por getCurrentAccessToken → refresh.
          retry = Math.max(retry, 1200);
        }
        scheduleReconnect();
      };
      ws.onmessage = (event: MessageEvent<string>) => {
        let message: RealtimeMessage;
        try {
          message = JSON.parse(event.data) as RealtimeMessage;
        } catch {
          return;
        }
        if (message.type === "notification.ws.ready") {
          setConnected(true);
          return;
        }
        if (message.type === "notification.created") {
          // Badge en vivo: incrementa el unread-count cacheado y refresca
          // inbox; las pantallas interesadas escuchan el CustomEvent.
          qc.setQueryData<{ count: number } | undefined>(
            ["notifications", "unread-count", tenantKey],
            (current) =>
              current ? { count: current.count + 1 } : { count: 1 },
          );
          void qc.invalidateQueries({ queryKey: ["notifications"] });
          window.dispatchEvent(
            new CustomEvent<RealtimeNotification>(NOTIFICATION_CREATED_EVENT, {
              detail: message,
            }),
          );
        }
      };
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { passive: true }),
    );
    if (document.visibilityState === "hidden") {
      // Montado en segundo plano (p. ej. pestaña abierta sin enfocar): espera a que vuelva.
      paused = true;
    } else {
      armIdleTimer();
      void connect();
    }
    return () => {
      closed = true;
      clearRetry();
      if (hiddenTimer) window.clearTimeout(hiddenTimer);
      if (idleTimer) window.clearTimeout(idleTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity),
      );
      socket?.close();
    };
  }, [qc, tenantKey, userId]);

  return { connected };
}
